import React, { useState, useEffect, useMemo } from 'react';
import { 
  DataGridPro, 
  GridActionsCellItem,
  useGridApiRef,
  useGridSelector,
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
  GridToolbarContainer
} from '@mui/x-data-grid-pro';
import { 
  Box, 
  Paper, 
  Typography,
  Tooltip,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  alpha,
  Chip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PieChartIcon from '@mui/icons-material/PieChart';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// Custom toolbar component
function FinancialGridToolbar(props) {
  const { onRefresh, showAnalytics } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <GridToolbarContainer sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      p: 2, 
      borderBottom: '1px solid #eaeaea' 
    }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button 
          startIcon={<FilterListIcon />} 
          size="small" 
          variant="outlined"
          color="primary"
        >
          Filters
        </Button>
        <Button 
          startIcon={<RefreshIcon />} 
          size="small"
          variant="outlined"
          color="primary"
          onClick={onRefresh}
        >
          Refresh
        </Button>
        <Button 
          startIcon={<PieChartIcon />} 
          size="small"
          variant="outlined"
          color="primary"
          onClick={showAnalytics}
        >
          Analytics
        </Button>
      </Box>
      
      <Box>
        <Button
          startIcon={<DownloadIcon />}
          size="small"
          variant="contained"
          color="primary"
          sx={{ mr: 1 }}
        >
          Export
        </Button>
        <IconButton
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem onClick={handleClose}>Customize Columns</MenuItem>
          <MenuItem onClick={handleClose}>Save View</MenuItem>
          <MenuItem onClick={handleClose}>Load Saved View</MenuItem>
          <Divider />
          <MenuItem onClick={handleClose}>Preferences</MenuItem>
        </Menu>
      </Box>
    </GridToolbarContainer>
  );
}

// Status cell renderer with color-coded chips
function StatusCell(props) {
  const { value } = props;
  const theme = useTheme();
  
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'active':
      case 'approved':
      case 'completed':
        return {
          bg: theme.palette.success.light,
          color: theme.palette.success.dark
        };
      case 'pending':
      case 'in review':
        return {
          bg: theme.palette.warning.light,
          color: theme.palette.warning.dark
        };
      case 'inactive':
      case 'rejected':
      case 'failed':
        return {
          bg: theme.palette.error.light,
          color: theme.palette.error.dark
        };
      default:
        return {
          bg: theme.palette.grey[200],
          color: theme.palette.grey[700]
        };
    }
  };
  
  const { bg, color } = getStatusColor(value);
  
  return (
    <Chip
      label={value}
      size="small"
      sx={{
        backgroundColor: bg,
        color: color,
        fontWeight: 600,
        fontSize: '0.75rem'
      }}
    />
  );
}

// Percentage cell with trend indicators
function PercentageCell(props) {
  const { value } = props;
  const theme = useTheme();
  
  const numValue = typeof value === 'string' ? 
    parseFloat(value.replace('%', '')) : 
    Number(value);
  
  const isPositive = numValue > 0;
  const isNegative = numValue < 0;
  const isZero = numValue === 0;
  
  const displayValue = typeof value === 'string' ? 
    value : 
    `${numValue.toFixed(2)}%`;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      color: isPositive ? theme.palette.success.main : 
             isNegative ? theme.palette.error.main : 
             theme.palette.text.primary
    }}>
      {isPositive && <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />}
      {isNegative && <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />}
      <Typography variant="body2" fontWeight={600}>
        {displayValue}
      </Typography>
    </Box>
  );
}

// Progress cell
function ProgressCell(props) {
  const { value } = props;
  const theme = useTheme();
  
  const numValue = typeof value === 'string' ? 
    parseFloat(value.replace('%', '')) : 
    Number(value);
  
  // Determine color based on progress value
  const getColor = (progress) => {
    if (progress < 30) return theme.palette.error.main;
    if (progress < 70) return theme.palette.warning.main;
    return theme.palette.success.main;
  };
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress
          variant="determinate"
          value={numValue}
          sx={{ 
            height: 8, 
            borderRadius: 5,
            backgroundColor: alpha(getColor(numValue), 0.2),
            '& .MuiLinearProgress-bar': {
              backgroundColor: getColor(numValue),
              borderRadius: 5,
            }
          }}
        />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(numValue)}%`}</Typography>
      </Box>
    </Box>
  );
}

// Currency cell formatter
function CurrencyCell(props) {
  const { value } = props;
  
  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'string' ? 
      parseFloat(amount.replace(/[^0-9.-]+/g, '')) : 
      Number(amount);
      
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numAmount);
  };
  
  return (
    <Typography 
      variant="body2" 
      fontWeight={600}
    >
      {formatCurrency(value)}
    </Typography>
  );
}

// SAMPLE DATA HARDCODED
const sampleTransactions = [
  {
    id: 1,
    transactionId: 'TRX-78945',
    date: '2025-04-01',
    accountName: 'Corporate Investments',
    category: 'Investment',
    amount: 250000,
    status: 'Completed',
    changePercent: 4.5,
    progress: 100,
    description: 'Q2 Strategic Investment'
  },
  {
    id: 2,
    transactionId: 'TRX-78946',
    date: '2025-04-02',
    accountName: 'Marketing Budget',
    category: 'Expense',
    amount: -45000,
    status: 'Completed',
    changePercent: -2.8,
    progress: 100,
    description: 'Q2 Marketing Campaign'
  },
  {
    id: 3,
    transactionId: 'TRX-78947',
    date: '2025-04-05',
    accountName: 'Revenue Account',
    category: 'Income',
    amount: 125000,
    status: 'Completed',
    changePercent: 8.3,
    progress: 100,
    description: 'Software License Sales'
  },
  {
    id: 4,
    transactionId: 'TRX-78948',
    date: '2025-04-07',
    accountName: 'Payroll',
    category: 'Expense',
    amount: -78500,
    status: 'Pending',
    changePercent: 0,
    progress: 65,
    description: 'Monthly Payroll'
  },
  {
    id: 5,
    transactionId: 'TRX-78949',
    date: '2025-04-08',
    accountName: 'Technology Budget',
    category: 'Expense',
    amount: -35000,
    status: 'In Review',
    changePercent: -1.2,
    progress: 45,
    description: 'New Server Infrastructure'
  },
  {
    id: 6,
    transactionId: 'TRX-78950',
    date: '2025-04-10',
    accountName: 'Client Services',
    category: 'Income',
    amount: 89500,
    status: 'Completed',
    changePercent: 5.7,
    progress: 100,
    description: 'Consulting Services'
  },
  {
    id: 7,
    transactionId: 'TRX-78951',
    date: '2025-04-12',
    accountName: 'R&D Budget',
    category: 'Expense',
    amount: -67000,
    status: 'Approved',
    changePercent: -3.5,
    progress: 85,
    description: 'Product Development'
  },
  {
    id: 8,
    transactionId: 'TRX-78952',
    date: '2025-04-15',
    accountName: 'Investment Portfolio',
    category: 'Investment',
    amount: 175000,
    status: 'Completed',
    changePercent: 7.2,
    progress: 100,
    description: 'Stock Purchase'
  },
  {
    id: 9,
    transactionId: 'TRX-78953',
    date: '2025-04-16',
    accountName: 'Operations',
    category: 'Expense',
    amount: -28500,
    status: 'Failed',
    changePercent: -4.8,
    progress: 25,
    description: 'Office Supplies'
  },
  {
    id: 10,
    transactionId: 'TRX-78954',
    date: '2025-04-18',
    accountName: 'Client A',
    category: 'Income',
    amount: 112000,
    status: 'Completed',
    changePercent: 6.1,
    progress: 100,
    description: 'Project Payment'
  },
  {
    id: 11,
    transactionId: 'TRX-78955',
    date: '2025-04-20',
    accountName: 'Client B',
    category: 'Income',
    amount: 93500,
    status: 'Pending',
    changePercent: 0,
    progress: 50,
    description: 'Service Retainer'
  },
  {
    id: 12,
    transactionId: 'TRX-78956',
    date: '2025-04-21',
    accountName: 'Travel & Entertainment',
    category: 'Expense',
    amount: -15800,
    status: 'Approved',
    changePercent: -0.9,
    progress: 75,
    description: 'Executive Travel'
  },
  {
    id: 13,
    transactionId: 'TRX-78957',
    date: '2025-04-22',
    accountName: 'Corporate Tax',
    category: 'Expense',
    amount: -235000,
    status: 'Completed',
    changePercent: -12.5,
    progress: 100,
    description: 'Quarterly Tax Payment'
  },
  {
    id: 14,
    transactionId: 'TRX-78958',
    date: '2025-04-25',
    accountName: 'Client C',
    category: 'Income',
    amount: 145000,
    status: 'Completed',
    changePercent: 9.8,
    progress: 100,
    description: 'Enterprise License'
  },
  {
    id: 15,
    transactionId: 'TRX-78959',
    date: '2025-04-28',
    accountName: 'Utilities',
    category: 'Expense',
    amount: -7500,
    status: 'In Review',
    changePercent: -0.4,
    progress: 60,
    description: 'Monthly Utilities'
  }
];

// Sample Column definitions
const sampleColumns = [
  { 
    field: 'transactionId', 
    headerName: 'Transaction ID', 
    width: 150 
  },
  { 
    field: 'date', 
    headerName: 'Date', 
    width: 120,
    type: 'date',
    valueFormatter: (params) => {
      if (!params.value) return '';
      return new Date(params.value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  },
  { 
    field: 'accountName', 
    headerName: 'Account', 
    width: 200 
  },
  { 
    field: 'category', 
    headerName: 'Category', 
    width: 150 
  },
  { 
    field: 'amount', 
    headerName: 'Amount', 
    width: 150,
    type: 'currency'
  },
  { 
    field: 'status', 
    headerName: 'Status', 
    width: 130,
    type: 'status'
  },
  { 
    field: 'changePercent', 
    headerName: 'Change %', 
    width: 130,
    type: 'percentage'
  },
  { 
    field: 'progress', 
    headerName: 'Progress', 
    width: 180,
    type: 'progress'
  },
  { 
    field: 'description', 
    headerName: 'Description', 
    width: 250 
  }
];

const FinancialDataGrid = ({ 
  title = 'Financial Dashboard',
  data = sampleTransactions, // Use hardcoded data as default
  columns = sampleColumns, // Use hardcoded columns as default
  height = 600,
  enableEditing = true,
  onRowUpdate,
  onRowDelete,
  onRefresh,
  loading = false,
  initialState = {},
  filterModel = { items: [] },
  paginationModel = { pageSize: 25, page: 0 },
  pageSizeOptions = [10, 25, 50, 100],
  showSummary = true
}) => {
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [gridData, setGridData] = useState(data);
  const [rowModesModel, setRowModesModel] = useState({});
  const [summaryData, setSummaryData] = useState({
    totalValue: 0,
    averageValue: 0,
    highestValue: 0,
    lowestValue: 0
  });
  
  // Format columns with custom cell renderers
  const enhancedColumns = useMemo(() => {
    const formattedColumns = columns.map(column => {
      // Apply custom renderers based on field type
      if (column.type === 'status' || column.field.includes('status')) {
        return {
          ...column,
          renderCell: StatusCell,
          width: column.width || 150
        };
      }
      
      if (column.type === 'percentage' || 
          column.field.includes('percentage') || 
          column.field.includes('change') ||
          column.field.includes('growth')) {
        return {
          ...column,
          renderCell: PercentageCell,
          width: column.width || 130
        };
      }
      
      if (column.type === 'progress' || 
          column.field.includes('progress') || 
          column.field.includes('completion')) {
        return {
          ...column,
          renderCell: ProgressCell,
          width: column.width || 180
        };
      }
      
      if (column.type === 'currency' || 
          column.field.includes('amount') || 
          column.field.includes('price') ||
          column.field.includes('revenue') ||
          column.field.includes('cost')) {
        return {
          ...column,
          renderCell: CurrencyCell,
          width: column.width || 150,
          align: 'right',
          headerAlign: 'right'
        };
      }
      
      return column;
    });
    
    if (enableEditing) {
      return [
        ...formattedColumns,
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          getActions: (params) => {
            const isInEditMode = rowModesModel[params.id]?.mode === 'edit';
            
            if (isInEditMode) {
              return [
                <GridActionsCellItem
                  icon={<Tooltip title="Save"><SaveIcon fontSize="small" /></Tooltip>}
                  label="Save"
                  onClick={() => handleSaveClick(params.id)}
                  sx={{ color: theme.palette.primary.main }}
                />,
                <GridActionsCellItem
                  icon={<Tooltip title="Cancel"><CancelIcon fontSize="small" /></Tooltip>}
                  label="Cancel"
                  onClick={() => handleCancelClick(params.id)}
                  sx={{ color: theme.palette.error.main }}
                />
              ];
            }
            
            return [
              <GridActionsCellItem
                icon={<Tooltip title="Edit"><EditIcon fontSize="small" /></Tooltip>}
                label="Edit"
                onClick={() => handleEditClick(params.id)}
                sx={{ color: theme.palette.primary.main }}
              />,
              <GridActionsCellItem
                icon={<Tooltip title="Delete"><DeleteIcon fontSize="small" /></Tooltip>}
                label="Delete"
                onClick={() => handleDeleteClick(params.id)}
                sx={{ color: theme.palette.error.main }}
              />
            ];
          }
        }
      ];
    }
    
    return formattedColumns;
  }, [columns, enableEditing, rowModesModel, theme]);

  // Update data and calculate summary when data changes
  useEffect(() => {
    setGridData(data);
    calculateSummary(data);
  }, [data]);
  
  const calculateSummary = (data) => {
    // Find numerical columns that could represent financial values
    const numericalFields = columns
      .filter(col => 
        col.type === 'number' || 
        col.type === 'currency' || 
        col.field.includes('amount') ||
        col.field.includes('value') ||
        col.field.includes('price') ||
        col.field.includes('revenue')
      )
      .map(col => col.field);
    
    if (numericalFields.length === 0) return;
    
    // Use first numerical field for summary calculations
    const primaryField = numericalFields[0];
    
    // Calculate summary values
    const values = data
      .map(row => {
        const val = row[primaryField];
        return typeof val === 'string' ? 
          parseFloat(val.replace(/[^0-9.-]+/g, '')) : 
          Number(val);
      })
      .filter(val => !isNaN(val));
    
    if (values.length === 0) return;
    
    const totalValue = values.reduce((sum, val) => sum + val, 0);
    const averageValue = totalValue / values.length;
    const highestValue = Math.max(...values);
    const lowestValue = Math.min(...values);
    
    setSummaryData({
      totalValue,
      averageValue,
      highestValue,
      lowestValue
    });
  };

  // Handle editing
  const handleEditClick = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: 'edit', fieldToFocus: columns[0].field }
    });
  };

  const handleSaveClick = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: 'view' }
    });
    
    const updatedRow = gridData.find(row => row.id === id);
    
    if (onRowUpdate && updatedRow) {
      onRowUpdate(updatedRow);
    }
  };

  const handleCancelClick = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: 'view', ignoreModifications: true }
    });
  };

  // Row update handler
  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow };
    
    setGridData(gridData.map(row => 
      row.id === newRow.id ? updatedRow : row
    ));
    
    calculateSummary(gridData.map(row => 
      row.id === newRow.id ? updatedRow : row
    ));
    
    return updatedRow;
  };

  // Handle deleting
  const handleDeleteClick = (id) => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      const newData = gridData.filter(row => row.id !== id);
      setGridData(newData);
      calculateSummary(newData);
      
      if (onRowDelete) {
        onRowDelete(id);
      }
    }
  };
  
  // Handle refresh
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };
  
  // Show analytics modal
  const handleShowAnalytics = () => {
    alert('Analytics view would open here');
  };

  // Format currency for summary display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Chip 
            icon={<LockIcon fontSize="small" />}
            label="Restricted" 
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          <IconButton size="small" onClick={() => alert('Settings')}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      {showSummary && (
        <Box sx={{ 
          display: 'flex', 
          p: 2, 
          gap: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.03)
        }}>
          <Card sx={{ flexGrow: 1 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="text.secondary" variant="caption">
                Total Value
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatCurrency(summaryData.totalValue)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flexGrow: 1 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="text.secondary" variant="caption">
                Average
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatCurrency(summaryData.averageValue)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flexGrow: 1 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="text.secondary" variant="caption">
                Highest
              </Typography>
              <Typography variant="h6" fontWeight={600} color={theme.palette.success.main}>
                {formatCurrency(summaryData.highestValue)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flexGrow: 1 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="text.secondary" variant="caption">
                Lowest
              </Typography>
              <Typography variant="h6" fontWeight={600} color={theme.palette.error.main}>
                {formatCurrency(summaryData.lowestValue)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
      
      <Box sx={{ height, width: '100%' }}>
        <DataGridPro
          apiRef={apiRef}
          rows={gridData}
          columns={enhancedColumns}
          editMode="row"
          rowModesModel={rowModesModel}
          processRowUpdate={processRowUpdate}
          onRowEditStart={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onRowEditStop={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          slots={{
            toolbar: FinancialGridToolbar,
          }}
          slotProps={{
            toolbar: {
              onRefresh: handleRefresh,
              showAnalytics: handleShowAnalytics,
            },
          }}
          initialState={{
            ...initialState,
            pagination: {
              ...initialState.pagination,
              paginationModel,
            },
          }}
          filterMode="server"
          filterModel={filterModel}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          loading={loading}
          getRowClassName={(params) => 
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
          }
          sx={{
            boxShadow: 'none',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.text.secondary,
              fontWeight: 600,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${theme.palette.divider}`,
            },
            '& .even-row': {
              backgroundColor: theme.palette.background.paper,
            },
            '& .odd-row': {
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell--editing': {
              backgroundColor: alpha(theme.palette.warning.light, 0.15),
              padding: '16px',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          }}
        />
      </Box>
    </Paper>
  );
};

// Example usage with sample data
const FinancialDashboardExample = () => {
  return (
    <Box sx={{ height: 700, p: 3 }}>
      <FinancialDataGrid 
        title="Financial Transactions Dashboard"
        // Using default sample data and columns
      />
    </Box>
  );
};

export default FinancialDataGrid;